import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { MaskLines } from "./Reveal";
import { http, asList } from "../lib/api";

export const Hero = () => {
  const [stats, setStats] = useState({ v: 0, p: 0 });
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 80, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 80, damping: 18 });
  const { scrollY } = useScroll();
  const imgY = useTransform(scrollY, [0, 800], [0, 140]);

  useEffect(() => {
    http.get("/media", { params: { limit: 200 } }).then((r) => {
      const l = asList(r);
      setStats({ v: l.filter((i) => i.type === "video").length, p: l.filter((i) => i.type === "photo").length });
    }).catch(() => {});
  }, []);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section onMouseMove={onMove} className="relative min-h-[100svh] overflow-hidden pt-28" data-testid="hero-section">
      <div className="pointer-events-none absolute -left-40 top-20 h-[520px] w-[520px] rounded-full bg-rose-700/20 blur-[140px]" />
      <div className="mx-auto grid max-w-[1440px] items-center gap-14 px-5 pb-20 sm:px-10 lg:grid-cols-[1.15fr_1fr]">
        <div className="relative z-10">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="eyebrow mb-8 flex items-center gap-3">
            <span className="h-px w-10 bg-rose-500" /> Somente para adultos · 18+
          </motion.p>
          <h1 className="font-serif-d glow-text text-[3.2rem] font-semibold leading-[0.95] tracking-tight text-white sm:text-7xl lg:text-[6.6rem]" data-testid="hero-title">
            <MaskLines lines={["O desejo", <span key="i" className="italic text-rose-500">sem filtro,</span>, "em alta definição."]} />
          </h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8 }} className="mt-8 max-w-md text-base leading-relaxed text-slate-300 sm:text-lg">
            Vídeos e ensaios fotográficos selecionados e publicados por um único curador. Acesso livre, discreto e sem cadastro.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.8 }} className="mt-10 flex flex-wrap items-center gap-4">
            <Link to="/videos" data-testid="hero-videos-cta" className="group flex items-center gap-3 bg-rose-600 px-7 py-4 text-sm font-semibold uppercase tracking-[0.18em] text-white transition-colors hover:bg-rose-500">
              Assistir vídeos <ArrowUpRight size={16} className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </Link>
            <Link to="/fotos" data-testid="hero-photos-cta" className="border border-slate-600 px-7 py-4 text-sm uppercase tracking-[0.18em] text-slate-200 transition-colors hover:border-white hover:text-white">
              Ver fotos
            </Link>
          </motion.div>
          <div className="mt-14 flex gap-10 font-mono-d text-xs text-slate-400" data-testid="hero-stats">
            <span><b className="font-serif-d block text-3xl font-normal text-white">{stats.v}</b>vídeos</span>
            <span><b className="font-serif-d block text-3xl font-normal text-white">{stats.p}</b>fotos</span>
            <span><b className="font-serif-d block text-3xl font-normal text-rose-500">18+</b>verificado</span>
          </div>
        </div>
        <div className="relative [perspective:1400px]">
          <motion.div style={{ rotateX: rx, rotateY: ry }} initial={{ opacity: 0, scale: 0.92, clipPath: "inset(18% 18% 18% 18%)" }} animate={{ opacity: 1, scale: 1, clipPath: "inset(0% 0% 0% 0%)" }} transition={{ duration: 1.6, delay: 0.3, ease: [0.16, 1, 0.3, 1] }} className="relative aspect-[4/5] w-full overflow-hidden [transform-style:preserve-3d]">
            <motion.img style={{ y: imgY }} src="/img/m1.jpg" alt="" className="absolute inset-0 h-[115%] w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#08080a] via-transparent to-rose-900/20" />
            <div className="absolute inset-0 shadow-[inset_0_0_120px_rgba(0,0,0,0.9)]" />
          </motion.div>
          <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1.3, duration: 0.9 }} className="absolute -bottom-6 -left-4 border border-rose-900/50 bg-black/80 p-5 backdrop-blur-xl sm:-left-10">
            <p className="eyebrow mb-1">Curadoria</p>
            <p className="font-serif-d text-xl italic text-white">Nova coleção</p>
          </motion.div>
          <div className="absolute -right-2 -top-4 flex h-24 w-24 items-center justify-center rounded-full border border-rose-500/50 sm:-right-6">
            <span className="pulse-ring absolute inset-0 rounded-full border border-rose-500/50" />
            <span className="font-serif-d text-2xl text-rose-400">18+</span>
          </div>
        </div>
      </div>
    </section>
  );
};
